import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NotePencil, Tag, Trash } from '@phosphor-icons/react';
import { IconButton, Typography } from '@la/ds-ui-components';
import {
  CartItemAddOn,
  CartItemOptionsSubprogramsDetail,
  CartItemOptionsSubprogramsRegistration,
  CartItemSubType,
  DeleteOperation,
} from '@la/types';
import { formatAsUSD, getLAHostnameParts } from '@la/utilities';
import { useConsolidatedCheckout } from 'lib/context/ConsolidatedCheckoutContext/ConsolidatedCheckoutContext';
import {
  useDeletePlayerStaffRegistrationMutation,
  useDeleteRegistrationAddOnMutation,
} from 'redux/services/checkoutApi';
import RemoveFromCartModal from '../RemoveFromCartModal/RemoveFromCartModal';
import styles from './OrderSummaryCard.module.scss';

/* ProgramRegistrationsTable */
export default function ProgramRegistrationsTable({
  addOns,
  cartItemUuid,
  subprograms,
  subtype,
}: Readonly<{
  addOns: CartItemAddOn[];
  cartItemUuid: string;
  subprograms: CartItemOptionsSubprogramsDetail[];
  subtype: CartItemSubType;
}>) {
  switch (subtype) {
    case 'add-ons':
      return (
        <RegistrationAddOnsTable addOns={addOns} cartItemUuid={cartItemUuid} />
      );
    case 'team':
      return (
        <TeamProgramRegistrationsTable
          cartItemUuid={cartItemUuid}
          subprograms={subprograms}
        />
      );
    case 'player':
    case 'staff':
      return (
        <PlayerStaffRegistrationsTable
          cartItemUuid={cartItemUuid}
          subprograms={subprograms}
          subtype={subtype}
        />
      );
    default:
      return null;
  }
}
/* */

/* RegistrationAddOnsTable */
function RegistrationAddOnsTable({
  addOns,
  cartItemUuid,
}: Readonly<{ addOns: CartItemAddOn[]; cartItemUuid: string }>) {
  const headers: string[] = [
    'Product',
    'Registrant',
    'Program',
    'Qty.',
    'Total price',
    '',
    '',
  ];
  return (
    <table
      aria-label="registration add-ons"
      className={styles.programRegistrationsTable}
    >
      <ProgramRegistrationsTableHeader
        cartItemUuid={cartItemUuid}
        headers={headers}
      />
      <RegistrationAddOnsTableBody
        addOns={addOns}
        cartItemUuid={cartItemUuid}
      />
    </table>
  );
}
/* */

/* RegistrationAddOnsTableBody */
function RegistrationAddOnsTableBody({
  addOns,
  cartItemUuid,
}: Readonly<{ addOns: CartItemAddOn[]; cartItemUuid: string }>) {
  return (
    <tbody>
      {addOns.map((addOn) => (
        <RegistrationAddOnsTableRow
          {...addOn}
          cartItemUuid={cartItemUuid}
          key={addOn.productInstanceId}
        />
      ))}
    </tbody>
  );
}
/* */

/* RegistrationAddOnsTableRow */
function RegistrationAddOnsTableRow({
  basePrice,
  cartItemUuid,
  description,
  productImageUrl,
  productInstanceId,
  productName,
  quantity,
  registrationId,
  required: isRequiredProduct,
  subprogramName,
  properties,
  userName,
}: Readonly<CartItemAddOn & { cartItemUuid: string }>) {
  const [showImage, setShowImage] = useState<boolean>(true);
  const [isProcessingRemoval, setIsProcessingRemoval] = useState(false);
  const [showRemoveFromCartModal, setShowRemoveFromCartModal] = useState(false);
  const [deleteRegistrationAddOn] = useDeleteRegistrationAddOnMutation();
  const { subdomain } = getLAHostnameParts();
  const { handleRegistrationAddOnRemoval } = useConsolidatedCheckout();
  const navigate = useNavigate();

  let price;
  const basePriceFloat = parseFloat(basePrice || '0.0');
  if (basePriceFloat) {
    price = formatAsUSD(basePriceFloat);
  } else {
    price = isRequiredProduct ? 'Included' : 'Free';
  }

  const selections = properties
    ?.map((property) => property.name || property.value)
    ?.join(', ');

  const addOnStepUrl = `/app/registration/add-ons?registrationId=${registrationId}`;
  function handleRegistrationAddOnDeletion() {
    setIsProcessingRemoval(true);
    deleteRegistrationAddOn({
      subdomain: subdomain,
      cartItemId: cartItemUuid,
      operation: DeleteOperation.AddOn,
      productInstanceId: productInstanceId,
    })
      .unwrap()
      .then(() => {
        handleRegistrationAddOnRemoval(productInstanceId);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setShowRemoveFromCartModal(false);
      });
  }

  return (
    <tr>
      <td>
        <span className={styles.product}>
          {showImage ? (
            <span className={styles.productImage}>
              <img
                alt={description || productName}
                onError={() => setShowImage(false)}
                src={productImageUrl}
              />
            </span>
          ) : (
            <span className={styles.productIcon}>
              <Tag
                fill="var(--blue-grey-400)"
                height="20"
                width="20"
                weight="bold"
              />
            </span>
          )}
          <span className={styles.productDetails}>
            <Typography size="medium" variant="ui">
              {productName}
            </Typography>
            <Typography
              className={styles.productSelections}
              size="small"
              variant="ui"
            >
              {selections}
            </Typography>
          </span>
        </span>
      </td>
      <td>{userName}</td>
      <td>{subprogramName}</td>
      <td>{quantity}</td>
      <td>{price}</td>
      <td>
        <IconButton
          ariaLabel="Edit"
          icon={<NotePencil weight="bold" />}
          onClick={() => navigate(addOnStepUrl)}
          variant="text"
        />
      </td>
      <td>
        {!isRequiredProduct ? (
          <IconButton
            ariaLabel="Delete"
            icon={<Trash weight="bold" />}
            onClick={() => setShowRemoveFromCartModal(true)}
            variant="text"
          />
        ) : null}
      </td>
      {showRemoveFromCartModal ? (
        <RemoveFromCartModal
          closeModal={() => setShowRemoveFromCartModal(false)}
          isOpen={showRemoveFromCartModal}
          isProcessing={isProcessingRemoval}
          mainMessage={`Are you sure you want to remove ${productName} from your cart? This action cannot be undone.`}
          modalTitle="Remove add-on?"
          removeCartItem={handleRegistrationAddOnDeletion}
          secondaryMessage=""
        />
      ) : null}
    </tr>
  );
}
/* */

/* TeamProgramRegistrationsTable */
function TeamProgramRegistrationsTable({
  cartItemUuid,
  subprograms,
}: Readonly<{
  cartItemUuid: string;
  subprograms: CartItemOptionsSubprogramsDetail[];
}>) {
  const headers: string[] = ['Program', 'Team', 'Total price'];
  return (
    <table
      className={styles.programRegistrationsTable}
      aria-label="team program registrations"
    >
      <ProgramRegistrationsTableHeader
        cartItemUuid={cartItemUuid}
        headers={headers}
      />
      <TeamProgramRegistrationsTableBody subprograms={subprograms} />
    </table>
  );
}
/* */

/* TeamProgramRegistrationsTableBody */
function TeamProgramRegistrationsTableBody({
  subprograms,
}: Readonly<{ subprograms: CartItemOptionsSubprogramsDetail[] }>) {
  return (
    <tbody>
      {subprograms.map(({ subprogramName, registrations }, idx) => (
        <TeamProgramRegistrationsTableRow
          key={`t-${subprogramName}-${idx}`}
          subprogramName={subprogramName}
          registrations={registrations}
        />
      ))}
    </tbody>
  );
}

/* */

/* TeamProgramRegistrationsTableRow */
function TeamProgramRegistrationsTableRow({
  subprogramName,
  registrations,
}: Readonly<{
  subprogramName: string;
  registrations: CartItemOptionsSubprogramsRegistration[];
}>) {
  return (
    <>
      {registrations.map(({ basePrice, registrationId, teamName }) => (
        <tr key={registrationId}>
          <td>{subprogramName}</td>
          <td>{teamName}</td>
          <td>${basePrice}</td>
        </tr>
      ))}
    </>
  );
}
/* */

/* PlayerStaffRegistrationsTable */
function PlayerStaffRegistrationsTable({
  cartItemUuid,
  subprograms,
  subtype,
}: Readonly<{
  cartItemUuid: string;
  subprograms: CartItemOptionsSubprogramsDetail[];
  subtype: string;
}>) {
  const headers = [];
  if (subtype === 'player') {
    headers.push('Player');
  } else if (subtype === 'staff') {
    headers.push('Staff', 'Role');
  }
  headers.push('Status', 'Program', 'Team', 'Total price', '', '');

  return (
    <table
      className={styles.programRegistrationsTable}
      aria-label={`${subtype} program registrations`}
    >
      <ProgramRegistrationsTableHeader
        cartItemUuid={cartItemUuid}
        headers={headers}
      />
      <PlayerStaffRegistrationsTableBody
        cartItemUuid={cartItemUuid}
        subprograms={subprograms}
        subtype={subtype}
      />
    </table>
  );
}
/* */

/* PlayerStaffRegistrationsTableBody */
function PlayerStaffRegistrationsTableBody({
  cartItemUuid,
  subprograms,
  subtype,
}: Readonly<{
  cartItemUuid: string;
  subprograms: CartItemOptionsSubprogramsDetail[];
  subtype: string;
}>) {
  return (
    <tbody>
      {subprograms.map(
        ({ subprogramId, subprogramName, registrations }, idx) => (
          <PlayerStaffRegistrationsTableRow
            cartItemUuid={cartItemUuid}
            key={`ps-${subprogramName}-${idx}`}
            registrations={registrations}
            subprogramId={subprogramId}
            subprogramName={subprogramName}
            subtype={subtype}
          />
        )
      )}
    </tbody>
  );
}

/* */

/* PlayerStaffRegistrationsTableRow */
function PlayerStaffRegistrationsTableRow({
  cartItemUuid,
  registrations,
  subprogramId,
  subprogramName,
  subtype,
}: Readonly<{
  cartItemUuid: string;
  registrations: CartItemOptionsSubprogramsRegistration[];
  subprogramId: number;
  subprogramName: string;
  subtype: string;
}>) {
  return (
    <>
      {registrations.map(
        (registration: CartItemOptionsSubprogramsRegistration) => (
          <tr key={registration.registrationId}>
            <td>{registration.userName}</td>
            {subtype === 'staff' ? <td>{registration.role}</td> : null}
            <td>{registration.teamName ? 'Team' : 'Free Agent'}</td>
            <td>{subprogramName}</td>
            <td>{registration.teamName}</td>
            <td>${registration.basePrice}</td>
            <RegistrationActions
              cartItemUuid={cartItemUuid}
              registration={registration}
              subprogramId={subprogramId}
            />
          </tr>
        )
      )}
    </>
  );
}
/* */

/*ProgramRegistrationsTableHeader */
function ProgramRegistrationsTableHeader({
  cartItemUuid,
  headers,
}: Readonly<{ cartItemUuid: string; headers: string[] }>) {
  return (
    <thead>
      <tr>
        {headers.map((header, idx) => {
          const headerCellKey =
            header !== ''
              ? `${header}-${cartItemUuid}`
              : `header-${idx}-${cartItemUuid}`;
          return <th key={headerCellKey}>{header}</th>;
        })}
      </tr>
    </thead>
  );
}
/* */

/* RegistrationActions */
function RegistrationActions({
  cartItemUuid,
  registration,
  subprogramId,
}: Readonly<{
  cartItemUuid: string;
  registration: CartItemOptionsSubprogramsRegistration;
  subprogramId: number;
}>) {
  const { subdomain } = getLAHostnameParts();
  const { handleRegistrationRemoval } = useConsolidatedCheckout();
  const [deletePlayerStaffRegistration] =
    useDeletePlayerStaffRegistrationMutation();
  const [isProcessingRemoval, setIsProcessingRemoval] = useState(false);
  const [showRemoveItemModal, setShowRemoveItemModal] = useState(false);

  function onEditButtonClick() {
    window.location.href = `/app/registration/review?registrationId=${registration.registrationId}`;
  }

  function handlePlayerStaffRegistrationDeletion() {
    setIsProcessingRemoval(true);
    deletePlayerStaffRegistration({
      cartItemId: cartItemUuid,
      operation: DeleteOperation.PlayerStaff,
      registration: registration,
      subdomain: subdomain,
      subprogramId: subprogramId,
    })
      .unwrap()
      .then(() => {
        handleRegistrationRemoval(
          cartItemUuid,
          registration.registrationId,
          subprogramId
        );
        setShowRemoveItemModal(false);
      });
  }

  return (
    <>
      <td>
        <IconButton
          ariaLabel="Edit"
          icon={<NotePencil weight="bold" />}
          onClick={onEditButtonClick}
          variant="text"
        />
      </td>
      <td>
        <IconButton
          ariaLabel="Delete"
          icon={<Trash weight="bold" />}
          onClick={() => setShowRemoveItemModal(true)}
          variant="text"
        />
      </td>
      {showRemoveItemModal ? (
        <RemoveFromCartModal
          closeModal={() => setShowRemoveItemModal(false)}
          isOpen={showRemoveItemModal}
          isProcessing={isProcessingRemoval}
          mainMessage="You are now removing this item from your cart, all registration progress will be lost."
          modalTitle="Remove item"
          primaryActionLabel="Yes, remove this"
          removeCartItem={handlePlayerStaffRegistrationDeletion}
          secondaryMessage="Are you sure you want to continue?"
        />
      ) : null}
    </>
  );
}
/* */
